import React, { ErrorInfo } from 'react';
import {
  init,
  reactRouterV6BrowserTracingIntegration,
  withSentryReactRouterV6Routing,
  setUser,
  captureException,
  setTags as setSentryTags,
} from '@sentry/react';
import { Primitive } from '@sentry/core';
import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

export const reportError = (error: Error, errorInfo?: ErrorInfo) => {
  captureException(error, {
    extra: { info: JSON.stringify(errorInfo) },
  });
};

export const setTags = (tags: { [key: string]: Primitive }) => {
  setSentryTags(tags);
};

export const isPreviewLink = () => {
  const previewLinkRegex = /\/index\.[\da-f]{40}\.html$/;
  return previewLinkRegex.test(window.location.pathname);
};

const SENTRY_DNS =
  'https://1c98d40640804fd2b55f99dd9d1a4e37@o10683.ingest.sentry.io/4503921308270592';

export const initializeErrorReporting = () => {
  // Warn if commit hash is missing - this helps debug build issues
  if (!import.meta.env.COMMIT_HASH) {
    console.warn(
      '[Sentry] COMMIT_HASH is undefined - errors will be tagged as "unknown" release'
    );
  }

  init({
    dsn: SENTRY_DNS,
    enabled: process.env.NODE_ENV !== 'development',
    release: import.meta.env.COMMIT_HASH || 'unknown',
    environment: isPreviewLink()
      ? 'preprod'
      : import.meta.env.APPCUES_ENV || 'staging',
    integrations: [
      reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],

    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 0.25,
  });
};

export const setUserId = (userId?: string) => {
  setUser({ id: userId });
};

export const RoutesWithErrorReporting = withSentryReactRouterV6Routing(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Routes as any // `any` is necessary because of the npm hoisting behavior causing type mismatch
);
